import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { PLAYER } from "@/data/portfolio";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        {/* Left text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <p
            data-testid="hero-overline"
            className="text-xs md:text-sm font-semibold uppercase tracking-[0.3em] text-neutral-500 mb-6 md:mb-8 flex items-center gap-3"
          >
            <span className="inline-block w-8 h-px bg-[#e63946]" />
            {PLAYER.position} · No. {PLAYER.number}
          </p>

          <h1
            data-testid="hero-name"
            aria-label={`${PLAYER.firstName} ${PLAYER.lastName}.`}
            className="font-display font-black uppercase leading-[0.92] tracking-tighter text-[16vw] sm:text-[14vw] md:text-[10vw] lg:text-[8.5vw]"
          >
            {PLAYER.firstName}{" "}
            <br />
            <span className="text-[#e63946]">{PLAYER.lastName}.</span>
          </h1>

          <p className="mt-8 md:mt-10 max-w-xl text-base md:text-lg leading-relaxed text-neutral-700">
            {PLAYER.tagline}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <a
              href="#highlights"
              data-testid="hero-cta-highlights"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#0a0a0a] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#e63946] transition-colors"
            >
              Watch highlights
              <ArrowDownRight size={16} />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="text-xs font-semibold uppercase tracking-widest text-neutral-900 link-underline"
            >
              Contact / Scout enquiries
            </a>
          </div>
        </motion.div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="lg:col-span-5 relative"
          data-testid="hero-image-wrap"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-100">
            <img
              src={PLAYER.heroImage}
              alt="Athlete portrait"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white font-display font-bold uppercase text-xs tracking-widest mix-blend-difference">
              <span>KICKSTART FC</span>
              <span>INDIA</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats bar */}
      <div
        data-testid="hero-stats"
        className="mt-20 md:mt-28 border-t border-b border-neutral-200 grid grid-cols-2 md:grid-cols-4"
      >
        {PLAYER.stats.map((s, i) => (
          <div
            key={s.label}
            className={`py-8 px-6 md:px-8 ${
              i !== PLAYER.stats.length - 1 ? "md:border-r border-neutral-200" : ""
            } ${i % 2 === 0 ? "border-r md:border-r" : ""} ${
              i < 2 ? "border-b md:border-b-0 border-neutral-200" : ""
            }`}
          >
            <div className="font-display text-4xl md:text-5xl font-black tracking-tighter">
              {s.value}
            </div>
            <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-500">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
